import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    UiAlertBanner,
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import {
    useModalState,
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { PortalPeoplePicker } from '@uipath/portal-shell-react';
import type { DirectoryEntry } from '@uipath/portal-shell-types';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import omit from 'lodash/omit';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    Link,
    useParams,
} from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import { v4 as uuidv4 } from 'uuid';

import useCheckAuthTypes from '../../../auth/hooks/CheckAuthType';
import useUserInfo from '../../../auth/hooks/UserInfo';
import {
    notificationType,
    QUOTA_REACHED_MESSAGE,
} from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import { useCheckAADEnabled } from '../../../common/hooks/useCheckAADEnabled';
import useIsServicesPageEnabled from '../../../common/hooks/useIsServicesPageEnabled';
import { useLicenseExpired } from '../../../common/hooks/useLicenseExpired';
import { useOrchRoleAssignmentForAX } from '../../../common/hooks/useOrchRoleAssignmentForAX';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import type {
    IDirectoryEntry,
    ResolveDirectoryEntityType,
} from '../../../common/interfaces/cis/directory';
import { DirectoryEntityType } from '../../../common/interfaces/cis/directory';
import type { IGroup } from '../../../common/interfaces/cis/group';
import type { SourceFilters } from '../../../services/identity/DirectoryService';
import type {
    ICreateGroupPayload,
    IEditGroupPayload,
    IGroupPayload,
} from '../../../services/identity/GroupService';
import {
    createGroup,
    editGroup,
    getGroupById,
    groupUrl,
} from '../../../services/identity/GroupService';
import {
    getRules,
    ruleUrl,
} from '../../../services/identity/RuleService';
import { AX_DIRECTORY_ID } from '../../../services/orchestrator/OrchRoleAssignmentService';
import {
    accountGlobalId,
    EnableUserLicensingSelector,
    isAdminSelector,
    profile,
    userGlobalId,
} from '../../../store/selectors';
import { decodeSanitizedHtml } from '../../../util/DecodeSanitizedHtml';
import { UiDrawer } from '../../common/UiDrawer';
import UiForm from '../../common/UiForm';
import { UserGroup } from '../../common/UserGroups';
import { ConfirmCreateEditGroupSuccessComponent } from './SuccessMessageComponents';

const useStyles = makeStyles(theme =>
    createStyles({
        body: {
            margin: '0px 24px',
            flex: '1 1 100%',
        },
        input: { marginTop: 20 },
        inputLabel: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputMargin: { marginBottom: '12px' },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        cancelButton: { marginRight: '10px' },
        licenseContent: {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            height: 'calc(100vh - 54px)',
        },
        closeButton: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '12px',
            padding: '12px',
            borderTop: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        },
        provisioningRuleBody: {
            fontSize: '10px',
            lineHeight: '16px',
            color: theme.palette.semantic.colorForeground,
            marginBottom: '4px',
        },
        provisioningRuleLinkContainer: { display: 'inline-flex' },
        provisioningRuleLink: {
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundLink,
        },
        spacingTop: { marginTop: '20px' },
    }),
);

interface ICreateEditGroupData {
    name: string;
    groupMembers: Array<IDirectoryEntry | string>;
}

const CreateEditGroupComponent: React.FC<{ type: 'add' | 'edit' }> = ({ type }) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const createNotification = useUiSnackBar();
    const setErrorMessage = useCentralErrorSetter();

    const { getErrorMessage } = useGetErrorInfo();

    const navigate = useNavigateWithParams();
    const { id } = useParams<{ id?: string }>();
    const getRoute = useRouteResolver();
    const isServicesPageEnabled = useIsServicesPageEnabled();
    const isAdmin = useSelector(isAdminSelector);
    const partitionGlobalId = useSelector(accountGlobalId);
    const EnableUserLicensing = useSelector(EnableUserLicensingSelector);
    const { checkTokenTypeIsAuth0: isAuth0 } = useCheckAuthTypes();
    const { token } = useUserInfo();
    const {
        showUseOrchRoleAssignmentDialogForAX, isLoadingTenantOrchRoleInfo,
    } = useOrchRoleAssignmentForAX({ fetchRoles: id === AX_DIRECTORY_ID });

    const currentUserId = useSelector(userGlobalId);
    const currentProfile = useSelector(profile);

    const {
        open, close,
    } = useModalState(RouteNames.Groups);

    const AADConnectionEnabledOnOrganization = useCheckAADEnabled();
    const isExpiredLicense = useLicenseExpired();

    const dataUrl = useMemo(() => {
        const baseUrl = EnableUserLicensing && isAdmin ? `${groupUrl}/licenses` : `${groupUrl}`;
        return `${baseUrl}?partitionGlobalId=${partitionGlobalId}`;
    }, [ EnableUserLicensing, isAdmin, partitionGlobalId ]);

    const { mutate } = useSWRConfig();

    const {
        data: group,
        isValidating,
        error,
    } = useSWR(
        id && type === 'edit' ? {
            url: groupUrl,
            partitionGlobalId,
            id,
        } : null,
        getGroupById,
    );

    const {
        data: rules,
        isValidating: rulesLoading,
    } = useSWR(
        type === 'edit' ? {
            url: ruleUrl,
            partitionGlobalId,
        } : null,
        getRules,
    );

    const [ loading, setLoading ] = useState(false);
    const [ showConfirmCreate, setShowConfirmCreate ] = useState(false);
    const [ createdGroup, setCreatedGroup ] = useState<IGroup | undefined>();

    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        getValues,
        watch,
        setValue,
        formState: {
            isDirty,
            isValid,
            errors,
        },
    } = useForm<ICreateEditGroupData>({
        mode: 'onChange',
        defaultValues: {
            name: '',
            groupMembers: [],
        },
    });

    const sourceFilters = useMemo(() => [
        'localUsers',
        'directoryUsers',
        'directoryGroups',
        'robotAccounts',
        'applications',
    ] as SourceFilters[], []);

    const watchedGroupMembers = watch('groupMembers');

    const singleAdmin = useMemo(() =>
        group?.name === 'Administrators' && watchedGroupMembers.length < 1
    , [ watchedGroupMembers, group?.name ]);

    useEffect(() => {
        if (type === 'edit' && group) {
            reset({
                name: group.name ? decodeSanitizedHtml(group.name) : '',
                groupMembers:
                group.members?.map(member => ({
                    identifier: member.identifier,
                    identityName: member.name,
                    displayName: member.displayName,
                    email: member.email,
                    objectType: member.objectType as ResolveDirectoryEntityType,
                    source: member.source,
                    type: member.objectType?.toLowerCase().includes('group')
                        ? DirectoryEntityType.group
                        : DirectoryEntityType.user,
                    chipType: 'resolved',
                })) ?? [],
            });
        }
    }, [ reset, type, group ]);

    const rulesForGroup = useMemo(() => rules?.filter(rule => {
        let groupsInRule: string[] = [];
        try {
            groupsInRule = JSON.parse(rule.definition as string)?.GroupsToAssign;
            // eslint-disable-next-line no-empty
        } catch {}
        return id && groupsInRule.includes(id) && rule.enabled;
    }), [ id, rules ]);

    const onSubmit = useCallback(
        async (data: ICreateEditGroupData) => {
            setLoading(true);

            const payload: IGroupPayload = {
                partitionGlobalId,
                name: data.name.trim(),
            };

            try {
                if (type === 'add') {
                    const createGroupPayload: ICreateGroupPayload = {
                        ...payload,
                        id: uuidv4(),
                        directoryUserMemberIDs: data.groupMembers.map(member =>
                            typeof member !== 'string' ? member.identifier : member,
                        ),
                    };

                    const isCurrentUser = isString(data.groupMembers?.[0])
                        ? (data.groupMembers as string[]).indexOf(currentUserId) > -1
                        : !!(data.groupMembers as IDirectoryEntry[]).find(g => g.identifier === currentUserId);

                    const groupCreated = await createGroup(createGroupPayload, isCurrentUser);
                    setCreatedGroup(groupCreated);
                } else {
                    if (!group) {
                        throw new Error('Group must be defined when editing a group');
                    }
                    const addedMembers = data.groupMembers.filter(
                        user =>
                            !group.members?.find(x =>
                                typeof user !== 'string' ? x.identifier === user.identifier : user === x.email,
                            ),
                    );
                    const removedMembers =
                        group.members?.filter(
                            user =>
                                !data.groupMembers
                                    .map(x => x as IDirectoryEntry)
                                    .find(dataUser => dataUser.identifier === user.identifier),
                        ) ?? [];

                    if (
                        !AADConnectionEnabledOnOrganization &&
                        group.id === UserGroup.Administrators &&
                        addedMembers.length === 0 &&
                        removedMembers.length === group.members.length
                    ) {
                        setError('groupMembers', { type: 'noAdmins' });
                        setLoading(false);
                        return;
                    }
                    const isAdded = !!addedMembers.find(m =>
                        typeof m !== 'string' ? m.identifier === currentUserId : m === currentProfile.emailId,
                    );
                    const isCurrentUser = isAdded || !!removedMembers.find(m => m.identifier === currentUserId);
                    const editGroupPayload: IEditGroupPayload = {
                        ...payload,
                        directoryUserIDsToAdd: addedMembers.map(member =>
                            typeof member !== 'string' ? member.identifier : member,
                        ),
                        directoryUserIDsToRemove: removedMembers.map(entry => entry.identifier),
                    };
                    await editGroup(group.id, editGroupPayload, {
                        type: isAdded ? 'create' : 'delete',
                        isCurrentUser,
                    });
                }
            } catch (err) {
                const response = (err as any)?.response;
                if (response?.status === 409) {
                    setError('name', { type: 'conflict' });
                    setLoading(false);
                } else if (response?.status === 400 && response?.data?.length) {
                    setErrorMessage(
                        <div>
                            <UiText>
                                {translate({ id: 'CLIENT_GROUP_RESOLVE_ERROR' })}
                            </UiText>
                            <UiText>
                                {response.data.join(', ')}
                            </UiText>
                        </div>,
                    );
                    setError('groupMembers', { type: 'unresolved' });
                    setLoading(false);
                } else if (response?.data?.Message === QUOTA_REACHED_MESSAGE) {
                    setErrorMessage(translate({ id: 'CLIENT_QUOTA_REACHED' }));
                } else {
                    setErrorMessage(await getErrorMessage(err));
                }
                return;
            }

            setLoading(false);
            mutate({
                url: dataUrl,
                partitionGlobalId,
            });
            if (type === 'add' && (EnableUserLicensing || isServicesPageEnabled)) {
                setShowConfirmCreate(true);
            } else {
                if (type === 'edit' && id === AX_DIRECTORY_ID) {
                    showUseOrchRoleAssignmentDialogForAX();
                }
                createNotification(
                    translate(
                        { id: type === 'edit' ? 'CLIENT_GROUP_UPDATED' : 'CLIENT_GROUP_CREATED' },
                        { 0: data.name && decodeSanitizedHtml(data.name) },
                    ),
                    notificationType.SUCCESS
                );
                close(true);
            }
        },
        [
            partitionGlobalId,
            mutate,
            dataUrl,
            type,
            EnableUserLicensing,
            isServicesPageEnabled,
            currentUserId,
            group,
            AADConnectionEnabledOnOrganization,
            setError,
            currentProfile.emailId,
            setErrorMessage,
            translate,
            getErrorMessage,
            createNotification,
            close,
            showUseOrchRoleAssignmentDialogForAX,
            id,
        ],
    );

    const peoplePickerChangedCallback = useCallback((event: any) => {
        const prevWithoutChip =
                    getValues('groupMembers')?.map(chip => {
                        if (typeof chip === 'string') {
                            return chip;
                        }
                        return omit(chip, [ 'optionId', 'chipType' ]);
                    }) ?? [];
        const curWithoutChip = event.detail.data.map((chip: IDirectoryEntry) => omit(chip, [ 'optionId', 'chipType' ]));
        const valuesChanged = !isEqual(prevWithoutChip, curWithoutChip);
        setValue('groupMembers', event.detail.data, {
            shouldDirty: valuesChanged,
            shouldValidate: valuesChanged,
        });
    }, [ getValues, setValue ]);

    const peoplePickerErrorCallback = useCallback((event: any) => {
        if (event.detail) {
            setError('groupMembers', { type: 'invalid' });
        } else {
            clearErrors('groupMembers');
        }
    }, [ clearErrors, setError ]);

    const peoplePickerLoadingCallback = useCallback((event: any) => setLoading(event.detail), []);

    return (
        <UiDrawer
            title={
                EnableUserLicensing && showConfirmCreate
                    ? translate({ id: 'CLIENT_GROUP_SUCCESSFULLY_CREATED' })
                    : type === 'add'
                        ? translate({ id: 'CLIENT_GROUPS_ADD_GROUP' })
                        : translate({ id: 'CLIENT_GROUPS_EDIT_GROUP' })
            }
            drawerProps={{
                anchor: 'right',
                open,
                onClose: () => close(showConfirmCreate),
            }}
            loading={isValidating || rulesLoading || isLoadingTenantOrchRoleInfo}
        >
            {error ? (
                <div className={classes.body}>
                    <UiText>
                        {translate({ id: 'CLIENT_UNKNOWN_ERROR_FROMBACKEND' })}
                    </UiText>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            window.location.reload();
                        }}
                        style={{ marginTop: '12px' }}
                    >
                        {translate({ id: 'CLIENT_RETRY' })}
                    </Button>
                </div>
            ) : showConfirmCreate ? (
                EnableUserLicensing ? (
                    <div className={classes.licenseContent}>
                        <div className={classes.body}>
                            <UiText style={{
                                fontSize: '16px',
                                marginTop: '16px',
                            }}>
                                {translate({ id: 'CLIENT_WHATS_NEXT' })}
                            </UiText>
                            <UiText style={{
                                fontWeight: 600,
                                marginTop: '8px',
                            }}>
                                {translate({ id: 'CLIENT_CREATE_LICENSE_ALLOCATION_RULE' })}
                            </UiText>
                            <UiText>
                                {translate({ id: 'CLIENT_LICENSE_ALLOCATION_SUCCESS_ONE' })}
                            </UiText>
                            <br />
                            <UiText>
                                {translate({ id: 'CLIENT_LICENSE_ALLOCATION_SUCCESS_TWO' })}
                            </UiText>
                            {createdGroup && (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    style={{ marginTop: '12px' }}
                                    onClick={() =>
                                        navigate(`${getRoute(RouteNames.Groups)}/allocations/add`,
                                            {
                                                state: {
                                                    group: createdGroup,
                                                    previousLocation: getRoute(RouteNames.Groups),
                                                },
                                            }
                                        )}
                                    data-cy="add-edit-create-allocation-button"
                                    disabled={isExpiredLicense}
                                >
                                    {translate({ id: 'CLIENT_ALLOCATE_RULE' })}
                                </Button>
                            )}
                        </div>
                        <div className={classes.closeButton}>
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => close(true)}>
                                {translate({ id: 'CLIENT_CLOSE' })}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <ConfirmCreateEditGroupSuccessComponent close={close} />
                )
            ) : (
                <UiForm
                    onSubmit={handleSubmit(onSubmit)}
                    actions={
                        <>
                            {!showConfirmCreate && (
                                <div className={classes.actions}>
                                    <Button
                                        className={classes.cancelButton}
                                        onClick={() => close()}
                                        color="primary"
                                        data-cy='add-edit-cancel-button'>
                                        {translate({ id: 'CLIENT_CANCEL' })}
                                    </Button>
                                    <UiProgressButton
                                        loading={loading}
                                        disabled={!isDirty || !isValid || singleAdmin}
                                        onClick={handleSubmit(onSubmit)}
                                        variant="contained"
                                        data-cy="add-edit-submit-button"
                                    >
                                        {type === 'add' ? translate({ id: 'CLIENT_ADD' }) : translate({ id: 'CLIENT_SAVE' })}
                                    </UiProgressButton>
                                </div>
                            )}
                        </>
                    }
                    isDrawer
                >
                    <div data-cy="AAD-warning-text">
                        {AADConnectionEnabledOnOrganization && isAuth0 && (
                            <UiAlertBanner
                                type="info"
                                closeable={false}>
                                {translate({ id: 'CLIENT_CANNOT_SEARCH_AZURE_AD_DIRECTORY' })}
                            </UiAlertBanner>
                        )}
                    </div>

                    <div className={classes.input}>
                        <UiText
                            className={clsx(classes.inputLabel, classes.inputMargin)}
                            id="groupNameLabel">
                            {translate({ id: 'CLIENT_GROUP_NAME' })}
                        </UiText>
                        <Controller
                            control={control}
                            name="name"
                            rules={{
                                required: true,
                                maxLength: 256,
                            }}
                            render={({ field }) =>
                                <TextField
                                    {...field}
                                    variant="outlined"
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={
                                        (errors.name?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })) ||
                                        (errors.name?.type === 'maxLength' && translate({ id: 'CLIENT_GROUPS_MAX_GROUP_NAME_ERROR' })) ||
                                        (errors.name?.type === 'conflict' && translate({ id: 'CLIENT_GROUP_ALREADY_EXISTS' }))
                                    }
                                    disabled={group?.type === 0}
                                    InputProps={{ className: 'Tall' }}
                                    data-cy="add-edit-group-name"
                                    inputProps={{ 'aria-labelledby': 'groupNameLabel' }}
                                />}
                        />
                    </div>
                    <div
                        className={classes.input}
                        data-cy="groups-people-picker">
                        <Controller
                            control={control}
                            name="groupMembers"
                            render={({ field }) =>
                                <PortalPeoplePicker
                                    token={process.buildConfigs.passPeoplePickerToken ? (token || undefined) : undefined}
                                    sourceFilters={sourceFilters}
                                    value={field.value as DirectoryEntry[]}
                                    minHeight="72px"
                                    maxHeight="124px"
                                    onPeoplePickerChanged={peoplePickerChangedCallback}
                                    onPeoplePickerError={peoplePickerErrorCallback}
                                    onPeoplePickerLoading={peoplePickerLoadingCallback}
                                    onPeoplePickerResolving={peoplePickerLoadingCallback}
                                />}
                        />
                        {singleAdmin &&
                        <UiText className={classes.spacingTop}>
                            {translate({ id: 'CLIENT_ONLY_ONE_ADMIN' })}
                        </UiText> }
                    </div>
                    {!!rulesForGroup?.length &&
                        <div className={classes.input}>
                            <UiText className={classes.inputLabel}>
                                {translate({ id: 'CLIENT_SAML_JIT_PROVISIONING_RULES' }, { count: rulesForGroup.length })}
                            </UiText>
                            <UiText>
                                {translate({ id: 'CLIENT_SAML_JIT_PROVISIONING_RULES_DESCRIPTION' })}
                            </UiText>
                            <UiText className={classes.provisioningRuleLinkContainer}>
                                {rulesForGroup
                                    .map((rule, index) =>
                                        <React.Fragment key={`${rule.name}-${rule.id}`}>
                                            <Link
                                                className={classes.provisioningRuleLink}
                                                to={getRoute(`${RouteNames.SecuritySettingsSAMLProvisioningRules}/edit/${rule.id}`)}>
                                                {rule.name}
                                            </Link>
                                            {index !== rulesForGroup.length - 1 &&
                                            <UiText>
                                                ,&nbsp;
                                            </UiText>}
                                        </React.Fragment>)}
                            </UiText>
                        </div>}
                </UiForm>
            )}
        </UiDrawer>
    );
};

export const CreateEditGroupComponentWithParams: React.FC = () => {
    const { type } = useParams<{ type: 'add' | 'edit' }>();

    return <CreateEditGroupComponent type={type ?? 'add'} />;
};

export default CreateEditGroupComponent;
